import React, { useState, useEffect } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { getLeague, getTeam, getPlayers, setAuctionTeam } from '../../data';
import {
    CircularProgress, Button, Select, MenuItem, TextField,
    Typography, Paper, Checkbox, FormControlLabel, Grid,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckIcon from '@material-ui/icons/Check';

function OverwriteXI(props) {
    const leagueID = props.match.params.leagueID;
    const teamID = props.match.params.teamID;

    const [loading, setLoading] = useState(true);
    const [league, setLeague] = useState(null);
    const [teamData, setTeamData] = useState(null);
    const [playerBios, setPlayerBios] = useState([]);
    const [gameWeek, setGameWeek] = useState('');
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const leagueData = await getLeague(leagueID);
                setLeague(leagueData);
                const team = await getTeam(leagueID, teamID);
                setTeamData(team);
                const bios = await getPlayers(leagueData.tournament, leagueID);
                setPlayerBios(bios.players || []);
            } catch (err) {
                setError(err.message);
            }
            setLoading(false);
        };
        load();
    }, [leagueID, teamID]);

    const squad = teamData?.squad || [];
    const gameWeeks = teamData
        ? Object.keys(teamData).filter(k => /^gw\d+$/i.test(k)).sort((a, b) => {
            return parseInt(b.replace(/gw/i, '')) - parseInt(a.replace(/gw/i, ''));
        })
        : [];

    const handleGameWeekChange = (e) => {
        const gw = e.target.value;
        setGameWeek(gw);
        setSuccess(false);
        setError(null);
        if (teamData && teamData[gw]) {
            setSelectedPlayers(teamData[gw].players.map(p => p.id));
        } else {
            setSelectedPlayers([]);
        }
    };

    const handlePlayerToggle = (playerID) => {
        setSelectedPlayers(prev => {
            if (prev.includes(playerID)) {
                return prev.filter(id => id !== playerID);
            } else {
                return [...prev, playerID];
            }
        });
        setSuccess(false);
    };

    const getPlayerName = (playerID) => {
        const bio = playerBios.find(p => p.id === playerID);
        return bio ? `${bio.name} (${bio.team} - ${bio.pos})` : playerID;
    };

    const handleSave = async () => {
        if (!gameWeek) {
            setError('Select a gameweek');
            return;
        }
        const teamSize = league?.teamSize || 11;
        if (selectedPlayers.length !== teamSize) {
            setError(`Select exactly ${teamSize} players. Currently selected: ${selectedPlayers.length}`);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await setAuctionTeam(leagueID, teamID, gameWeek, selectedPlayers);
            setSuccess(true);
        } catch (err) {
            setError(err.message);
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
                <CircularProgress />
            </div>
        );
    }

    const teamSize = league?.teamSize || 11;
    const inXI = squad.filter(id => selectedPlayers.includes(id));
    const onBench = squad.filter(id => !selectedPlayers.includes(id));

    return (
        <div style={{ maxWidth: 600, margin: '0 auto', padding: 16, marginBottom: 60 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <Link to={`/leagues/${leagueID}/teams/${teamID}`} style={{ marginRight: 8 }}>
                    <ArrowBackIcon />
                </Link>
                <Typography variant="h5" style={{ flexGrow: 1 }}>
                    Overwrite XI - {teamID}
                </Typography>
            </div>

            {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
            {success && <Alert severity="success" style={{ marginBottom: 16 }}>XI saved successfully</Alert>}

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        select
                        fullWidth
                        label="Gameweek"
                        value={gameWeek}
                        onChange={handleGameWeekChange}
                        SelectProps={{
                            SelectDisplayProps: { 'data-testid': 'gameweek-select' }
                        }}
                    >
                        {gameWeeks.map(gw => (
                            <MenuItem key={gw} value={gw}>{gw.toUpperCase()}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        Selected: {selectedPlayers.length} / {teamSize}
                    </Typography>
                </Grid>
            </Grid>

            {gameWeek && (
                <>
                    <Paper style={{ padding: 16, marginTop: 16 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Playing XI ({inXI.length})
                        </Typography>
                        {inXI.map(playerID => (
                            <FormControlLabel
                                key={playerID}
                                control={
                                    <Checkbox
                                        checked={true}
                                        onChange={() => handlePlayerToggle(playerID)}
                                        color="primary"
                                        inputProps={{ 'data-testid': `player-${playerID}` }}
                                    />
                                }
                                label={getPlayerName(playerID)}
                                style={{ display: 'block' }}
                            />
                        ))}
                    </Paper>

                    <Paper style={{ padding: 16, marginTop: 16 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Bench ({onBench.length})
                        </Typography>
                        {onBench.map(playerID => (
                            <FormControlLabel
                                key={playerID}
                                control={
                                    <Checkbox
                                        checked={false}
                                        onChange={() => handlePlayerToggle(playerID)}
                                        color="primary"
                                        inputProps={{ 'data-testid': `player-${playerID}` }}
                                    />
                                }
                                label={getPlayerName(playerID)}
                                style={{ display: 'block' }}
                            />
                        ))}
                    </Paper>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={saving || selectedPlayers.length !== teamSize}
                        style={{ marginTop: 16 }}
                        data-testid="save-xi-button"
                    >
                        {saving ? 'Saving...' : 'Save XI'}
                    </Button>
                </>
            )}
        </div>
    );
}

export default withRouter(OverwriteXI);
